import { ReactElement, useMemo } from 'react'

import { Tooltip } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Flex, Icon } from 'components/core'
import { WhitelabelLogoIcon } from 'components/Icons'
import { getOrganizationTheme } from 'helpers/organizationTheme'
import CobaltLogo from 'images/logo/CobaltLogo'
import CobaltLogoMark from 'images/logo/CobaltLogoMark'
import { getPath } from 'routing/pagePaths'
import { Organization } from 'types'

import * as styles from './styles.module.scss'

interface Props {
    currentOrganization?: Organization
    isSidebarCollapsed: boolean
    toggleSidebarCollapsed: () => void
}

const COBALT_LOGO_HEIGHT = 26
const COBALT_EXPANDED_LOGO_WITH_WHITE_LABEL_HEIGHT = 24
const COBALT_COLLAPSED_LOGO_WITH_WHITE_LABEL_HEIGHT = 30

const Branding = ({
    currentOrganization,
    isSidebarCollapsed,
    toggleSidebarCollapsed,
}: Props): ReactElement => {
    const { t } = useTranslation()
    const orgTheme = getOrganizationTheme(currentOrganization)
    const whiteLabelLogoUrl = orgTheme?.logo_url
    const whiteLabelLogoCondensedUrl = orgTheme?.logo_url_condensed

    const cobaltLogoContainer = useMemo(
        () => (
            <Flex
                className={classNames(styles.brandLogoRow, {
                    [styles.brandLogoRowCollapsed]: isSidebarCollapsed,
                })}
                direction="row"
                justifyContent={isSidebarCollapsed ? 'center' : 'space-between'}
                alignItems="center"
            >
                <Link to={getPath('homePage')} id="cobalt-logo-link">
                    {isSidebarCollapsed ? (
                        <CobaltLogoMark
                            fill="#FFF"
                            style={{
                                height: whiteLabelLogoUrl
                                    ? COBALT_COLLAPSED_LOGO_WITH_WHITE_LABEL_HEIGHT
                                    : COBALT_LOGO_HEIGHT,
                            }}
                        />
                    ) : (
                        <CobaltLogo
                            className={styles.cobaltLogoExpanded}
                            fill="#FFF"
                            style={{
                                height: whiteLabelLogoUrl
                                    ? COBALT_EXPANDED_LOGO_WITH_WHITE_LABEL_HEIGHT
                                    : COBALT_LOGO_HEIGHT,
                                ...(!whiteLabelLogoUrl
                                    ? { position: 'relative', left: -3 }
                                    : {}),
                            }}
                        />
                    )}
                </Link>
                <Tooltip
                    title={
                        isSidebarCollapsed
                            ? t('common:expandSideMenu', 'Expand Menu')
                            : t('common:collapseSideMenu', 'Collapse Menu')
                    }
                    placement="right"
                >
                    <button
                        type="button"
                        className={classNames(styles.toggleSidebarButton, {
                            [styles.toggleSidebarButtonCollapsed]:
                                isSidebarCollapsed,
                            [styles.toggleSidebarButtonExpanded]:
                                !isSidebarCollapsed,
                        })}
                        id="toggle-sidebar-collapsed-button"
                        onClick={toggleSidebarCollapsed}
                    >
                        <Icon
                            name={
                                isSidebarCollapsed
                                    ? 'NAV_MENU_EXPAND'
                                    : 'NAV_MENU_COLLAPSE'
                            }
                            size={15}
                        />
                    </button>
                </Tooltip>
            </Flex>
        ),
        [isSidebarCollapsed, whiteLabelLogoUrl, toggleSidebarCollapsed, t]
    )

    return (
        <div className={styles.brandingContainer}>
            {whiteLabelLogoUrl ? (
                <Flex direction="column" alignItems="normal">
                    {cobaltLogoContainer}
                    <div className={styles.whitelabelLogoContainer}>
                        <Link to={getPath('homePage')}>
                            <WhitelabelLogoIcon
                                src={
                                    isSidebarCollapsed &&
                                    whiteLabelLogoCondensedUrl
                                        ? whiteLabelLogoCondensedUrl
                                        : whiteLabelLogoUrl
                                }
                                alt={currentOrganization?.name ?? ''}
                            />
                        </Link>
                    </div>
                </Flex>
            ) : (
                cobaltLogoContainer
            )}
        </div>
    )
}

export default Branding
